import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, Plus, Camera, Trash2, ChevronRight, Image as ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const SEV = {
  bassa: "bg-zinc-700/40 text-zinc-300 border-zinc-600",
  media: "bg-[#FF9500]/15 text-[#FF9500] border-[#FF9500]/40",
  alta: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40",
};
const STATO = {
  aperto: "bg-[#FF3B30]/15 text-[#FF3B30]",
  in_lavorazione: "bg-[#FF9500]/15 text-[#FF9500]",
  risolto: "bg-[#39FF14]/15 text-[#39FF14]",
};
const STATO_LABEL = { aperto: "Aperto", in_lavorazione: "In lavorazione", risolto: "Risolto" };

export function ReportPhoto({ path, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked;
    (async () => {
      if (!path) return;
      try { const r = await api.get(`/files/${path}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); revoked = u; setUrl(u); } catch (e) {}
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [path]);
  if (!url) return null;
  return <img src={url} alt="Danno" className={className} />;
}

export default function Reports() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [filter, setFilter] = useState("tutti");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ bike_id: "", descrizione: "", gravita: "media" });
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/reports");
    setReports(r.data);
  };
  useEffect(() => {
    (async () => {
      await load();
      if (hasPerm("inventory.view")) { try { const b = await api.get("/bikes"); setBikes(b.data); } catch (e) {} }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const bid = params.get("bike_id");
    if (bid) { setForm((f) => ({ ...f, bike_id: bid })); setOpen(true); }
    // eslint-disable-next-line
  }, [params]);

  const save = async () => {
    if (!form.bike_id) { toast.error("Seleziona una bici"); return; }
    if (!form.descrizione.trim()) { toast.error("Descrizione obbligatoria"); return; }
    setSaving(true);
    try {
      const res = await api.post("/reports", form);
      if (photo) {
        const fd = new FormData(); fd.append("file", photo);
        await api.post(`/reports/${res.data.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Segnalazione creata");
      setOpen(false); setForm({ bike_id: "", descrizione: "", gravita: "media" }); setPhoto(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const setStato = async (r, stato) => {
    try { await api.put(`/reports/${r.id}`, { stato }); await load(); toast.success("Stato aggiornato"); }
    catch (e) { toast.error("Errore"); }
  };
  const del = async (r) => {
    try { await api.delete(`/reports/${r.id}`); toast.success("Eliminata"); await load(); }
    catch (e) { toast.error("Errore"); }
  };

  const filtered = reports.filter((r) => filter === "tutti" ? true : r.stato === filter);

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Danni e guasti</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">SEGNALAZIONI</h1>
        </div>
        {hasPerm("reports.create") && (
          <button onClick={() => setOpen(true)} data-testid="add-report-btn"
            className="flex items-center gap-2 rounded-sm bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#cc2f26] transition-colors active:scale-95">
            <Plus className="h-5 w-5" /> Nuova segnalazione
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {["tutti", "aperto", "in_lavorazione", "risolto"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
            className={`px-4 py-2 rounded-sm text-sm border transition-colors ${filter === s ? "border-[#f98125] bg-[#f98125]/10 text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}>
            {s === "tutti" ? "Tutte" : STATO_LABEL[s]}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-zinc-600 text-sm py-16 text-center border border-dashed border-zinc-800 rounded-sm">Nessuna segnalazione.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`report-card-${r.id}`} className="bg-[#141414] border border-zinc-800 rounded-sm p-4 hover:border-zinc-700 transition-colors">
              <div className="flex items-start gap-4">
                {r.photo_path ? (
                  <ReportPhoto path={r.photo_path} className="h-16 w-16 object-cover rounded-sm shrink-0" />
                ) : (
                  <button onClick={() => navigate(`/bici/${r.bike_id}`)} className="h-16 w-16 shrink-0 bg-[#f98125]/15 rounded-sm flex items-center justify-center hover:bg-[#f98125]/30">
                    <span className="font-display text-2xl text-[#f98125]">{r.bike_numero}</span>
                  </button>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => navigate(`/bici/${r.bike_id}`)} className="font-display text-xl text-white hover:text-[#f98125] flex items-center gap-1">Bici {r.bike_numero} <ChevronRight className="h-4 w-4" /></button>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${SEV[r.gravita]}`}>{r.gravita}</span>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${STATO[r.stato]}`}>{STATO_LABEL[r.stato]}</span>
                  </div>
                  <p className="text-sm text-zinc-300 mt-1.5 line-clamp-3">{r.descrizione}</p>
                  <div className="text-xs text-zinc-600 mt-2">Segnalato da {r.created_by} · {new Date(r.created_at).toLocaleDateString("it-IT")}</div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800/60 gap-2">
                {hasPerm("reports.edit") ? (
                  <Select value={r.stato} onValueChange={(v) => setStato(r, v)}>
                    <SelectTrigger className="h-8 w-44 bg-[#0A0A0A] border-zinc-800 rounded-sm text-xs" data-testid={`report-stato-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-zinc-800">
                      <SelectItem value="aperto">Aperto</SelectItem>
                      <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
                      <SelectItem value="risolto">Risolto</SelectItem>
                    </SelectContent>
                  </Select>
                ) : <span />}
                {hasPerm("reports.delete") && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-report-${r.id}`} className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-zinc-800 rounded-sm"><Trash2 className="h-4 w-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#141414] border-zinc-800">
                      <AlertDialogHeader><AlertDialogTitle>Eliminare la segnalazione?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={() => del(r)} className="rounded-sm bg-[#FF3B30] hover:bg-[#cc2f26]">Elimina</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[#FF3B30]" /> Nuova segnalazione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Bici *</Label>
              <Select value={form.bike_id} onValueChange={(v) => setForm({ ...form, bike_id: v })}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="report-bike-select"><SelectValue placeholder="Seleziona la bici" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-zinc-800 max-h-64">
                  {bikes.map((b) => <SelectItem key={b.id} value={b.id}>Bici {b.numero} — {b.modello || "—"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Gravità</Label>
              <Select value={form.gravita} onValueChange={(v) => setForm({ ...form, gravita: v })}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="report-gravita-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-zinc-800">
                  <SelectItem value="bassa">Bassa</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Descrizione del danno *</Label>
              <Textarea value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} data-testid="report-desc"
                className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" rows={3} placeholder="Descrivi il danno o il guasto riscontrato..." />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Foto del danno (opzionale)</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <label className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm border border-[#f98125]/50 bg-[#f98125]/10 cursor-pointer hover:bg-[#f98125]/20 text-sm text-white">
                  <Camera className="h-4 w-4" /> Scatta foto
                  <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="report-camera-input" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
                </label>
                <label className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm border border-zinc-800 cursor-pointer hover:border-zinc-700 text-sm text-zinc-400">
                  <ImageIcon className="h-4 w-4" /> Galleria
                  <input type="file" accept="image/*" className="hidden" data-testid="report-photo-input" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
                </label>
              </div>
              {photo && (
                <div className="mt-2 flex items-center gap-2 text-xs text-[#39FF14]">
                  <Camera className="h-3.5 w-3.5" /> Foto selezionata: {photo.name}
                  <button onClick={() => setPhoto(null)} className="text-zinc-500 hover:text-[#FF3B30]">✕</button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={save} disabled={saving} data-testid="report-save"
              className="rounded-sm bg-[#FF3B30] text-white font-bold px-5 py-2.5 hover:bg-[#cc2f26] transition-colors disabled:opacity-50">{saving ? "Salvataggio..." : "Crea segnalazione"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
